import { User, Category, Media, Genre, NewsFeedItem, RadioChartCategory, Playlist, BPMPlaylist } from "../models";
import { APIVersion, AuthenticatedRequest, HTTPMethod, AuthenticatedFileRequest } from "../requests/request";
import { Album } from "../models/album";
import { Tag } from "../models/tag";
import { Curated } from "../models/curated";
import { CuratedSet } from "../models/curated_set";
import { Artist } from "../models/artist";
import { SearchSuggestion } from "../models/search_suggestion";
import MapUtils from "../requests/jsonparser";
import { PlaybackInfo } from "../models/media";
import { Podcast } from "../models/podcast";
import { PodcastResponse } from "../models/podcast_response";
import { BPMPlaylistResponse } from "../models/bpmplaylist_response";
import { SubscriptionInfo } from "../errors";

export enum RemixQueryOptions {
    All = 'all',
    Hide = 'hide',
    Only = 'only'
}

export enum SortByQueryOptions {
    DateDes = "date_des",
    DateAsc = "date_asc",
    ArtistDesc = "artisc_desc",
    ArtisAsc = "artist_asc",
    TitleDesc = "title_desc",
    TitleAsc = "title_asc",
    BPMDesc = "bpm_desc",
    BPMAsc = "bpm_asc"
}

export interface DurationParameters {
    duration: 'daily' | 'weekly' | 'monthly'
}

export interface QuerySearchParameters {
    category?: string
    sub_genre?: string
    show_only?: string
    bpm_max?: number
    bpm_min?: number
    remix?: RemixQueryOptions
    sort_by?: SortByQueryOptions
    keywords?: string
    key?: string

    serato?: ''
    'tr-sync'?: ''
    exclusive?: ''

    'attributes.mood'?: string
    'attributes.voice_gender'?: 'male' | 'female' | 'instrumental' | 'none'
    'attributes.voice_presence'?: 'medium' | 'high' | 'low'
    'attributes.energy_level'?: 'medium' | 'high' | 'low'
    'attributes.energy_dynamics'?: 'low' | 'high'
}

export enum MediaContentType {
    Video = 'video',
    Audio = 'audio'
}

export interface PagingInfoResponse {
    page: number;
    total: number;
}

export interface PagingInfo {
    skip: number;
    limit: number;
}

export interface BulkSearchResult {
    sets: CuratedSet,
    albums: Album[],
    news: NewsFeedItem[]
}
export interface Banner {
    id: string;
    image_url: string
    end_at: Date;
    start_at: Date;
    url: string;
    color: string;
    min_membership?: SubscriptionInfo
    position: number;
}

export interface VOD {
    id: string;
    name: string;
    description: string;
    url: string;
    order?: number
    is_featured?: boolean

    created_at: string
    updated_at: string
}
export class API {


    public static getLiveStatus(): Promise<{
        status: {
            create: 'Idle' | 'Live',
            supreme: 'Idle' | 'Live'
        }
        stream: { title: string }
        stream_url: string
        placeholder_stream_url: string
        placeholder_stream_url_mp4: string
    }> {
        let apiRequest = new AuthenticatedRequest<Banner>('/live/status', HTTPMethod.GET, APIVersion.v3_0);

        return apiRequest.send().then((response) => {
            return response.data;
        });
    }

    public static getVOD(): Promise<VOD[]> {
        let apiRequest = new AuthenticatedRequest<Banner>('/video-on-demand', HTTPMethod.GET, APIVersion.v3_0);

        return apiRequest.send().then((response) => {
            return response.data;
        });
    }

    public static openAPIlink(link: string): Promise<{ url: string }> {
        let apiRequest = new AuthenticatedRequest('/banners/link', HTTPMethod.GET, APIVersion.v3_0, { link });
        return apiRequest.send().then((response) => {
            return response.data;
        });
    }

    /**
     * get Banners
     */
    public static getBanner(): Promise<Banner[]> {
        let apiRequest = new AuthenticatedRequest<Banner>('/banners', HTTPMethod.GET, APIVersion.v3_0);

        return apiRequest.send().then((response) => {
            return <Banner[]>response.data;
        });
    }

    /**
     * Fetches the download url for an media object 
     * @returns {Promise<{url: string>} The Promise contains an APIError if rejected (DownloadLimitReachedError)
     */
    public static downloadMedia(media_id: string, crate: boolean, from: string, options?: { entity: string, entity_id: string }): Promise<{ url: string }> {
        let apiRequest = new AuthenticatedRequest<{ url: string }>('/media/' + media_id + '/download', HTTPMethod.GET, APIVersion.v1_2, { crate: crate ? 'true' : 'false', from, ...options });
        apiRequest.preventNotVerifiedHandler = true
        apiRequest.preventInvalidSubscriptionHandler = true
        return apiRequest.send().then((response) => {
            return <{ url: string }>response.data;
        });
    }

    /**
     * Fetches the recommendations for a media object
     * @returns {Promise<{artist?: Artist, songs: Album[]}>} The Promise contains an APIError if rejected
     */
    public static getMediaRecommendations(media_id: string): Promise<{ artist?: Artist, songs: Album[] }> {
        let apiRequest = new AuthenticatedRequest<Album>('/media/' + media_id + '/artistreleases', HTTPMethod.GET, APIVersion.v1_2);

        return apiRequest.send().then((response) => {
            return <{ artist?: Artist, songs: Album[] }>response.data;
        });
    }

    /**
     * Fetches the artists for a media object
     * @returns {Promise<Artist[]>} The Promise contains an APIError if rejected
     */
    public static getMediaArtists(media_id: string): Promise<Artist[]> {
        let apiRequest = new AuthenticatedRequest<Artist>('/media/' + media_id + '/artists', HTTPMethod.GET, APIVersion.v1_2);

        return apiRequest.send(Artist).then((response) => {
            return <Artist[]>response.data;
        });
    }

    /**
     * Fetches the albums for a artist
     * @returns {Promise<Album[]>} The Promise contains an APIError if rejected
     */
    public static getArtistAlbums(contentType: MediaContentType, artist: string, options: DurationParameters & QuerySearchParameters, paging: PagingInfo): Promise<{ data: Album[], pagination: PagingInfoResponse }> {
        let apiRequest = new AuthenticatedRequest<Album>('/artist/' + encodeURIComponent(artist) + '/albums/' + contentType, HTTPMethod.GET, APIVersion.v1_2, Object.assign({
            limit: paging.limit,
            skip: paging.skip
        }, options))

        return apiRequest.send(Album).then((response) => {
            return <{ data: Album[], pagination: PagingInfoResponse }>response;
        });
    }

    /**
    * Fetches the top downloads
    * @returns {Promise<Album[]>} The Promise contains an APIError if rejected
    */
    public static getDownloadHistory(contentType: MediaContentType, filter: { category?: string }, paging: { limit: number, skip: number }): Promise<{ data: Album[], pagination: PagingInfoResponse }> {
        let apiRequest = new AuthenticatedRequest<Album>('/download-history/' + contentType, HTTPMethod.GET, APIVersion.v1_2, { ...filter, ...paging })

        return apiRequest.send(Album).then((response) => {
            return <{ data: Album[], pagination: PagingInfoResponse }>response;
        });
    }

    /**
     * Fetches the top downloads
     * @returns {Promise<Album[]>} The Promise contains an APIError if rejected
     */
    public static getTopDownloads(contentType: MediaContentType, options: DurationParameters & QuerySearchParameters, paging: PagingInfo): Promise<{ data: Album[], pagination: PagingInfoResponse }> {
        let apiRequest = new AuthenticatedRequest<Album>('/topdownloads/' + contentType, HTTPMethod.GET, APIVersion.v1_2, Object.assign({
            limit: paging.limit,
            skip: paging.skip
        }, options))

        return apiRequest.send(Album).then((response) => {
            return <{ data: Album[], pagination: PagingInfoResponse }>response;
        });
    }

    /**
     * Fetches the top downloads
     * @returns {Promise<Album[]>} The Promise contains an APIError if rejected
     */
    public static getTopDownloadsPerCategory(contentType: MediaContentType, options: DurationParameters & QuerySearchParameters): Promise<Album[]> {
        let apiRequest = new AuthenticatedRequest<Album>('/topdownloads/' + contentType + '/perCategory', HTTPMethod.GET, APIVersion.v1_2, options)

        return apiRequest.send(Album).then((response) => {
            return <Album[]>response.data;
        });
    }

    /**
     * Fetches the top downloads for a given category
     * @returns {Promise<Album[]>} The Promise contains an APIError if rejected
     */
    public static getTopDownloadsForCategory(contentType: MediaContentType, categorySlug: string, options: DurationParameters & QuerySearchParameters, paging: PagingInfo): Promise<{ data: Album[], pagination: PagingInfoResponse }> {
        let apiRequest = new AuthenticatedRequest<Album>('/categories/' + categorySlug + '/topdownloads/' + contentType, HTTPMethod.GET, APIVersion.v1_2, Object.assign({
            limit: paging.limit,
            skip: paging.skip
        }, options));

        return apiRequest.send(Album).then((response) => {
            return <{ data: Album[], pagination: PagingInfoResponse }>response;
        });
    }

    /**
     * Fetches all RadioChartCategoroies
     * @returns {Promise<RadioChartCategory[]>} The Promise contains an APIError if rejected
     */
    public static getRadioChartCategories(): Promise<RadioChartCategory[]> {
        let apiRequest = new AuthenticatedRequest<RadioChartCategory>('/radiochartcategories', HTTPMethod.GET, APIVersion.v1_2);
        return apiRequest.send(RadioChartCategory).then((response) => {
            return <RadioChartCategory[]>response.data;
        });
    }

    /**
     * Fetches the radio charts
     * @returns {Promise<Album[]>} The Promise contains an APIError if rejected
     */
    public static getRadioCharts(contentType: MediaContentType, options: QuerySearchParameters & { radio_category_id?: number }, paging: PagingInfo): Promise<{ data: Album[], pagination: PagingInfoResponse }> {
        let apiRequest = new AuthenticatedRequest<Album>('/radiocharts/' + contentType, HTTPMethod.GET, APIVersion.v1_2, Object.assign({
            limit: paging.limit,
            skip: paging.skip
        }, options))

        return apiRequest.send(Album).then((response) => {
            return <{ data: Album[], pagination: PagingInfoResponse }>response;
        });
    }

    /**
     * Fetches the new releases
     * @returns {Promise<Album[]>} The Promise contains an APIError if rejected
     */
    public static getNewReleases(contentType: MediaContentType, options: QuerySearchParameters, paging: PagingInfo): Promise<{ data: Album[], pagination: PagingInfoResponse }> {
        let apiRequest = new AuthenticatedRequest<Album>('/newreleases/' + contentType, HTTPMethod.GET, APIVersion.v1_2, Object.assign({
            limit: paging.limit,
            skip: paging.skip
        }, options))

        return apiRequest.send(Album).then((response) => {
            return <{ data: Album[], pagination: PagingInfoResponse }>response;
        });
    }

    /**
     * Fetches the new releases
     * @returns {Promise<Album[]>} The Promise contains an APIError if rejected
     */
    public static getNewReleasesPerCategory(contentType: MediaContentType, options: QuerySearchParameters): Promise<{ data: Album[] }> {
        let apiRequest = new AuthenticatedRequest<Album>('/newreleases/' + contentType + '/perCategory', HTTPMethod.GET, APIVersion.v1_2, options)

        return apiRequest.send(Album).then((response) => {
            return <{ data: Album[] }>response;
        });
    }

    /**
     * Fetches the new releases
     * @returns {Promise<Album[]>} The Promise contains an APIError if rejected
     */
    public static getNewReleasesForCategory(contentType: MediaContentType, categorySlug: string, options: QuerySearchParameters, paging: PagingInfo): Promise<{ data: Album[], pagination: PagingInfoResponse }> {
        let apiRequest = new AuthenticatedRequest<Album>('/categories/' + encodeURIComponent(categorySlug) + '/newreleases/' + contentType, HTTPMethod.GET, APIVersion.v1_2, Object.assign({
            limit: paging.limit,
            skip: paging.skip
        }, options))

        return apiRequest.send(Album).then((response) => {
            return <{ data: Album[], pagination: PagingInfoResponse }>response;
        });
    }

    /**
     * Fetches the search results
     * @returns {Promise<Album[]>} The Promise contains an APIError if rejected
     */
    public static getSearch(contentType: MediaContentType, criteria: QuerySearchParameters, paging: PagingInfo): Promise<{ data: Album[], pagination: PagingInfoResponse }> {
        let apiRequest = new AuthenticatedRequest<Album>('/search/' + contentType, HTTPMethod.GET, APIVersion.v1_2, Object.assign(criteria, {
            limit: paging.limit,
            skip: paging.skip
        }));

        return apiRequest.send(Album).then((response) => {
            return <{ data: Album[], pagination: PagingInfoResponse }>response;
        });
    }

    /**
     * Fetches the combined bucket search results
     * @returns {Promise<>} The Promise contains an APIError if rejected
     */
    public static getContentSearch(query: string, paging: PagingInfo): Promise<BulkSearchResult> {
        let apiRequest = new AuthenticatedRequest<Album>('/search-content', HTTPMethod.GET, APIVersion.v1_2, Object.assign({ search: query }, {
            limit: paging.limit,
            skip: paging.skip
        }));

        return apiRequest.send().then((response) => {
            let data: any = {
                news: response.data.news.map(i => MapUtils.deserialize(NewsFeedItem, i)),
                albums: response.data.albums.map(i => MapUtils.deserialize(Album, i)),
                sets: MapUtils.deserialize(CuratedSet, response.data.sets),

            }

            return <BulkSearchResult>data;
        });
    }

    /**
     * Fetches the playlists
     * @returns {Promise<CuratedSet>} The Promise contains an APIError if rejected
     */
    public static getBPMPlaylists(search?: string, onlyFeatured?: boolean): Promise<BPMPlaylistResponse> {
        let apiRequest = new AuthenticatedRequest<BPMPlaylistResponse>('/bpm_playlists', HTTPMethod.GET, APIVersion.v1_2, { search, onlyFeatured });

        return apiRequest.send(BPMPlaylistResponse).then((response) => {
            return <BPMPlaylistResponse>response.data;
        });
    }

    /**
     * Fetches the podcasts
     * @returns {Promise<CuratedSet>} The Promise contains an APIError if rejected
     */
    public static getPodcasts(search?: string): Promise<PodcastResponse> {
        let apiRequest = new AuthenticatedRequest<PodcastResponse>('/podcasts', HTTPMethod.GET, APIVersion.v1_2, { search });

        return apiRequest.send(PodcastResponse).then((response) => {
            return <PodcastResponse>response.data;
        });
    }

    /**
     * Fetches the curated sets
     * @returns {Promise<CuratedSet>} The Promise contains an APIError if rejected
     */
    public static getCuratedSets(search?: string): Promise<CuratedSet> {
        let apiRequest = new AuthenticatedRequest<CuratedSet>('/curated_sets', HTTPMethod.GET, APIVersion.v1_2, { search });

        return apiRequest.send(CuratedSet).then((response) => {
            return <CuratedSet>response.data;
        });
    }

    /**
    * Fetches the featured curated sets
    * @returns {Promise<CuratedSet>} The Promise contains an APIError if rejected
    */
    public static getFeaturedCuratedSets(): Promise<Curated[]> {
        let apiRequest = new AuthenticatedRequest<Curated>('/curated_sets/featured', HTTPMethod.GET, APIVersion.v1_2);

        return apiRequest.send(Curated).then((response) => {
            return <Curated[]>response.data;
        });
    }

    /**
     * Fetches the podcast
     * @returns {Promise<Curated>} The Promise contains an APIError if rejected
     */
    public static getPodcastSlug(slug: string): Promise<Podcast> {
        let apiRequest = new AuthenticatedRequest<Podcast>('/podcasts/slug/' + slug, HTTPMethod.GET, APIVersion.v1_2);

        return apiRequest.send(Podcast).then((response) => {
            return <Podcast>response.data;
        });
    }

    /**
     * Fetches the playlist
     * @returns {Promise<Curated>} The Promise contains an APIError if rejected
     */
    public static getBPMPlaylistBySlug(slug: string): Promise<BPMPlaylist> {
        let apiRequest = new AuthenticatedRequest<BPMPlaylist>('/bpm_playlists/slug/' + slug, HTTPMethod.GET, APIVersion.v1_2);

        return apiRequest.send(BPMPlaylist).then((response) => {
            return <BPMPlaylist>response.data;
        });
    }

    /**
     * Fetches the curated set
     * @returns {Promise<Curated>} The Promise contains an APIError if rejected
     */
    public static getCuratedBySlug(slug: string): Promise<{ set: Curated, related: Curated[] }> {
        let apiRequest = new AuthenticatedRequest<{ set: Curated, related: Curated[] }>('/curated_sets/slug/' + slug, HTTPMethod.GET, APIVersion.v3_0);

        return apiRequest.send().then((response) => {
            return <{ set: Curated, related: Curated[] }>response.data;
        });
    }

    /**
     * Fetches the songs for a podcast
     * @returns {Promise<Album[]>} The Promise contains an APIError if rejected
     */
    public static getPodcast(id: string): Promise<Album[]> {
        let apiRequest = new AuthenticatedRequest<Album>('/podcasts/' + id, HTTPMethod.GET, APIVersion.v1_2);

        return apiRequest.send(Album).then((response) => {
            return <Album[]>response.data;
        });
    }

    /**
     * Fetches the songs for a playlist
     * @returns {Promise<Album[]>} The Promise contains an APIError if rejected
     */
    public static getBPMPlaylist(id: string): Promise<Album[]> {
        let apiRequest = new AuthenticatedRequest<Album>('/bpm_playlists/' + id, HTTPMethod.GET, APIVersion.v1_2);

        return apiRequest.send(Album).then((response) => {
            return <Album[]>response.data;
        });
    }

    /**
     * Fetches the songs for a curated set
     * @returns {Promise<Album[]>} The Promise contains an APIError if rejected
     */
    public static getCuratedSet(id: string): Promise<Album[]> {
        let apiRequest = new AuthenticatedRequest<Album>('/curated_sets/' + id, HTTPMethod.GET, APIVersion.v1_2);

        return apiRequest.send(Album).then((response) => {
            return <Album[]>response.data;
        });
    }

    /**
     * Fetches the songs for a curated set
     * @returns {Promise<Album[]>} The Promise contains an APIError if rejected
     */
    public static getPlaybackInfo(mediaId: string, from: string, options?: { entity: string, entity_id: string }): Promise<PlaybackInfo> {
        let apiRequest = new AuthenticatedRequest<PlaybackInfo>('/media/' + mediaId + '/play', HTTPMethod.GET, APIVersion.v1_2, { ...options, from });

        return apiRequest.send().then((response) => {
            return <PlaybackInfo>response.data;
        });
    }

    /**
     * Sets secure cookies to access media
     * @returns {Promise<void>} The Promise contains an APIError if rejected
     */
    public static getCloudfrontCookie(): Promise<void> {
        let apiRequest = new AuthenticatedRequest('/media/cookie', HTTPMethod.GET, APIVersion.v1_2);

        return apiRequest.send().then((response) => {
            return void 0
        });
    }

    /**
     * Fetches the browse section
     * @param contentType
     * @param paging
     * @param categorySlug
     */
    public static getBrowse(contentType: MediaContentType, options: QuerySearchParameters, paging: PagingInfo): Promise<{ data: Album[], pagination: PagingInfoResponse }> {
        let apiRequest = new AuthenticatedRequest<Album>('/browse/' + contentType, HTTPMethod.GET, APIVersion.v1_2, Object.assign({
            limit: paging.limit,
            skip: paging.skip
        }, options))
        return apiRequest.send(Album).then((response) => {
            return <{ data: Album[], pagination: PagingInfoResponse }>response
        });
    }

    /**
     * Fetches the categories for a given content-type
     * @returns {Promise<Category[]>} The Promise contains an APIError if rejected
     */
    public static getCategories(contentType?: MediaContentType, scope?: string): Promise<Category[]> {
        let apiRequest = new AuthenticatedRequest<Category>(contentType ? '/categories/' + contentType : '/categories', HTTPMethod.GET, APIVersion.v1_2, scope ? { scope: scope } : undefined);

        return apiRequest.send(Category).then((response) => {
            return <Category[]>response.data;
        });
    }

    /**
     * Fetches all keys
     * @returns {Promise<string[]>} The Promise contains an APIError if rejected
     */
    public static getKeys(): Promise<string[]> {
        let apiRequest = new AuthenticatedRequest<string>('/keys', HTTPMethod.GET, APIVersion.v1_2, undefined);

        return apiRequest.send().then((response) => {
            return <string[]>response.data;
        });
    }

    /**
     * Fetches the sub genres for a given category
     * @returns {Promise<Category[]>} The Promise contains an APIError if rejected
     */
    public static getSubGenres(categorySlug: string, contentType?: MediaContentType): Promise<Genre[]> {
        let apiRequest = new AuthenticatedRequest<Genre>((contentType ? '/categories/' + contentType : '/categories') + '/' + categorySlug + '/subgenres', HTTPMethod.GET, APIVersion.v1_2, undefined);

        return apiRequest.send(Genre).then((response) => {
            return <Genre[]>response.data;
        });
    }

    /**
     * Fetches the top10
     * @returns {Promise<Album[]>} The Promise contains an APIError if rejected
     */
    public static getTop10(contentType: MediaContentType): Promise<Album[]> {
        let apiRequest = new AuthenticatedRequest<Album>('/top10/' + contentType, HTTPMethod.GET, APIVersion.v1_2)

        return apiRequest.send(Album).then((response) => {
            return response.data;
        });
    }

    /**
     * Fetches the sub genres for a given category
     * @returns {Promise<Genre[]>} The Promise contains an APIError if rejected
     */
    public static getAllSubGenres(contentType?: MediaContentType): Promise<Genre[]> {
        let apiRequest = new AuthenticatedRequest<Genre>('/subgenres' + (contentType ? ('/' + contentType) : ''), HTTPMethod.GET, APIVersion.v1_2, undefined);

        return apiRequest.send(Genre).then((response) => {
            return <Genre[]>response.data;
        });
    }

    /**
     * Fetches the sub genres for a given category
     * @returns {Promise<Genre[]>} The Promise contains an APIError if rejected
     */
    public static getTop5Genres(contentType?: MediaContentType): Promise<Genre[]> {
        let apiRequest = new AuthenticatedRequest<Genre>('/subgenres' + (contentType ? ('/' + contentType) : ''), HTTPMethod.GET, APIVersion.v1_2, { top5: true });

        return apiRequest.send(Genre).then((response) => {
            return <Genre[]>response.data;
        });
    }

    /**
     * Fetches all Tags required for the search UI
     * @returns {Promise<Tag[]>} The Promise contains an APIError if rejected
     */
    public static getTags(): Promise<Tag[]> {
        let apiRequest = new AuthenticatedRequest<Tag>('/tags', HTTPMethod.GET, APIVersion.v1_2);
        return apiRequest.send(Tag).then((response) => {
            return <Tag[]>response.data;
        });
    }

    /**
     * Get search suggestions based on the entered term
     * @returns {Promise<SearchSuggestion[]>} The Promise contains an APIError if rejected
     */
    public static getSearchSuggestions(term: string): Promise<SearchSuggestion[]> {
        let apiRequest = new AuthenticatedRequest<SearchSuggestion>('/suggest', HTTPMethod.GET, APIVersion.v1_2, { term });
        return apiRequest.send(SearchSuggestion).then((response) => {
            return <SearchSuggestion[]>response.data;
        });
    }

    /**
     * Reports a album artowrk as inappropriate. Only allowed as admin!
     * @returns {Promise<void>} The Promise contains an APIError if rejected
     */
    public static reportAlbumArtwork(album_id: number): Promise<void> {
        let apiRequest = new AuthenticatedRequest<SearchSuggestion>('/album/' + album_id + '/cover_art', HTTPMethod.DELETE, APIVersion.v1_2);
        return apiRequest.send().then((response) => {
            return void 0;
        });
    }

    /**
     * Fetches the featured albums
     * @returns {Promise<Album[]>} The Promise contains an APIError if rejected
     */
    public static getFeatured(): Promise<{ data: Album[] }> {
        let apiRequest = new AuthenticatedRequest<Album>('/browse/featured', HTTPMethod.GET, APIVersion.v1_2)

        return apiRequest.send(Album).then((response) => {
            return <{ data: Album[] }>response;
        });
    }

    /**
     * Fetches the featured bpm original albums
     * @returns {Promise<Album[]>} The Promise contains an APIError if rejected
     */
    public static getFeaturedBPMOriginals(): Promise<{ data: Album[] }> {
        let apiRequest = new AuthenticatedRequest<Album>('/browse/bpm_original', HTTPMethod.GET, APIVersion.v1_2)

        return apiRequest.send(Album).then((response) => {
            return <{ data: Album[] }>response;
        });
    }

    static savetempAlbum(album: Object) {
        let apiRequest = new AuthenticatedRequest<{}>('/admin/media/temp/save', HTTPMethod.POST, APIVersion.v1_0, undefined, album);
        return apiRequest.send().then((response) => {
            return response.data
        });
    }

    public static getMediaUploadLink(key: string): Promise<{ url: string, fields: Object }> {
        let apiRequest = new AuthenticatedRequest<{}>('/admin/media/temp/uploadLink', HTTPMethod.GET, APIVersion.v1_0, { key });
        return apiRequest.send().then((response) => {
            return response.data
        });
    }

    public static transcodeMedia(type: 'audio' | 'video', key: string): Promise<{ path: string }> {
        let apiRequest = new AuthenticatedRequest<{}>('/admin/media/temp/transcode/' + type, HTTPMethod.GET, APIVersion.v1_0, { key });
        return apiRequest.send().then((response) => {
            return response.data
        });
    }

    public static parseAudioInformation(key: string, album_name?: string): Promise<any> {
        let apiRequest = new AuthenticatedRequest<{}>('/admin/media/temp/parse/audio', HTTPMethod.GET, APIVersion.v1_0, { key, album_name });
        return apiRequest.send().then((response) => {
            return response.data
        });
    }

    public static parseVideoInformation(key: string, album_name?: string): Promise<any> {
        let apiRequest = new AuthenticatedRequest<{}>('/admin/media/temp/parse/video', HTTPMethod.GET, APIVersion.v1_0, { key, album_name });
        return apiRequest.send().then((response) => {
            return response.data
        });
    }

    public static updateMedia(album_id: number, media_id: number, data: Partial<Album>): Promise<Media> {
        let apiRequest = new AuthenticatedRequest<any>('/admin/album/' + album_id + '/media/' + media_id, HTTPMethod.PUT, APIVersion.v1_0, undefined, data);
        return apiRequest.send().then((response) => {
            return response.data
        });
    }

    public static checkMediaFileExisting(key: string, lastModified: number): Promise<{ url: string, fields: Object }> {
        let apiRequest = new AuthenticatedRequest<{}>('/admin/media/temp/checkFile', HTTPMethod.GET, APIVersion.v1_0, { key, lastModified });
        return apiRequest.send().then((response) => {
            return response.data
        });
    }

    public static approveAlbum(album: Album): Promise<Album> {
        let apiRequest = new AuthenticatedRequest<{}>('/admin/album/' + album.id + '/approve', HTTPMethod.PUT, APIVersion.v1_0);
        return apiRequest.send().then((response) => {
            return response.data
        });
    }

    public static getAlbumByNameAndArtist(name: string, artist: string): Promise<Album[]> {
        let apiRequest = new AuthenticatedRequest<{}>('/admin/album/byNameAndArtist', HTTPMethod.GET, APIVersion.v1_0, { name, artist });
        return apiRequest.send().then((response) => {
            return response.data
        });
    }

    public static getAlbumAnalytics(id: string, apiURL?: string): Promise<Album> {
        let apiRequest = new AuthenticatedRequest<{}>('/bpm-analytics/album/getById/' + id, HTTPMethod.GET, APIVersion.v1_0);
        if (apiURL) {
            apiRequest.overrideAPIUrl = apiURL
        }
        return apiRequest.send().then((response) => {
            return response.data
        });
    }


    public static getAlbumByid(id: number): Promise<Album> {
        let apiRequest = new AuthenticatedRequest<{}>('/album/' + id, HTTPMethod.GET, APIVersion.v1_0);
        return apiRequest.send().then((response) => {
            return response.data
        });
    }

    public static getSimilarAlbums(id: number): Promise<Album[]> {
        let apiRequest = new AuthenticatedRequest<{}>('/album/' + id + '/similar', HTTPMethod.GET, APIVersion.v1_0);
        return apiRequest.send().then((response) => {
            return response.data
        });
    }

    public static setAlbumCover(albumIds: string, image: File | Blob): Promise<void> {
        return Promise.all(albumIds.split(';').map(id => {
            let apiRequest = new AuthenticatedFileRequest<{}>('/admin/album/' + id + '/cover', HTTPMethod.PUT, APIVersion.v1_0, undefined, { image });
            return apiRequest.send().then((response) => {
                return response.data
            });
        })).then(() => {

        })
    }
}



// WEBPACK FOOTER //
// ./src/api/src/endpoints/media.ts